<template>
	<div class="app-sidebar-company-notifications">
		<div class="ao-h4 ao-text-secondary-color mb-4 px-8">
			{{ $t('Notification panel') }}
		</div>
		<v-progress-circular
			v-if="!dataLoaded"
			indeterminate
			color="primary"
			style="position: absolute; left: 50%; top: 50%; transform: translate3d(-50%, -50%, 0)"
		/>
		<div
			v-else
			class="ml-5 mr-2 my-2 mb-16"
		>
			<div
				v-for="notification in lastNotifications"
				:key="notification.id"
				class="mb-3 pa-2 ao-notification"
				@click="onOpenPreview(notification.id)"
			>
				<div class="text-textMuted ao-p3-regular d-flex justify-space-between">
					<div>{{ notification.category?.title }}</div>
					<div>{{ notification.created }}</div>
				</div>
				<div class="d-flex">
					<ao-icon :iconName="notification.notification_type?.iconKey" />
					<span
						:class="
							!readStatus
								? !notification.is_read
									? 'ao-title-notification'
									: 'ao-p2-regular'
								: 'ao-p2-regular'
						"
						class="text-textBody ml-2"
					>
						{{ notification.notification_type?.title }}
					</span>
				</div>
				<div
					class="text-textMuted ao-p3-regular ao-info-notification"
					v-html="notification.content"
				/>
			</div>
		</div>
		<div
			class="d-flex pl-7 pr-4 pb-4 pt-2 w-100 align-center"
			style="position: fixed; bottom: 0px; background-color: white"
		>
			<ao-btn
				:to="{
					name: 'CompanyGroupMyTasksModuleNotificationsTablePage',
					params: { companyId: selectedCompanyId ? selectedCompanyId : 0 },
				}"
				class="w-80 ao-p1 mr-4"
				color="primary"
				height="36"
				@click="onReadAll"
			>
				{{ $t('All notifications') }}
			</ao-btn>
			<v-menu
				v-model="modalState"
				open-on-click
				location="top"
			>
				<template v-slot:activator="{ props }">
					<ao-icon-btn
						v-bind="props"
						variant="flat"
						iconName="more"
						class="mt-1"
					/>
				</template>
				<v-card
					width="250"
					class="overflow-x-hidden ao-p1"
				>
					<ao-btn
						class="w-100"
						color="white"
						variant="flat"
						@click="onReadAll"
					>
						{{ $t('Mark as read') }}
					</ao-btn>
				</v-card>
			</v-menu>
		</div>
	</div>
	<PreviewSidebar
		:isActive="isActivePreview"
		@updateActiveState="togglePreviewState"
	>
		<template #preview-master-content>
			<NotificationsPreview
				ref="NotificationPreviewRef"
				:entityId="previewId"
			/>
		</template>
	</PreviewSidebar>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useTimeoutFn } from '@vueuse/core'

import useMoment from '@/composables/useMoment'

import { useUserStore } from '@/stores/userStore'
import { useCompanyNotificationStore } from '@/stores/companyNotificationStore'

import useNotifications from '@/composables/data/useNotifications'
import useNotificationCategoriesAndTypes from '@/composables/staticData/useNotificationCategoriesAndTypes'

import PreviewSidebar from '@/components/base/PreviewSidebar.vue'
import NotificationsPreview from '@/components/entities/notifications/NotificationsPreview.vue'
export default {
	name: 'SidebarNotifications',
	components: {
		PreviewSidebar,
		NotificationsPreview,
	},
	setup() {
		const {
			formatToFriendlyDateTime,
		} = useMoment()
		const companyNotificationStore = useCompanyNotificationStore()
		const { selectedCompanyId } = useUserStore()
		const { notificationCategories, notificationTypes } = useNotificationCategoriesAndTypes()
		const {
			notifications,
			getNotifications,
			updateNotificationsMarkReadStatus,
			updateNotificationsMarkAllRead,
		} = useNotifications()

		const lastNotifications = ref([])
		const currentIds = ref([])
		const readStatus = ref(false)
		const modalState = ref(false)
		const dataLoaded = ref(false)
		const isActivePreview = ref(false)
		const previewId = ref(0)
		const NotificationPreviewRef = ref(null)

		const loadNotifications = async () => {
			currentIds.value = []
			await getNotifications({ __ao__dateRange: 'lastThirtyDays' })

			lastNotifications.value = notifications.value.slice(0, 20).map((notification) => {
				currentIds.value.push(notification.id)

				const notificationTypeObj = notificationTypes.value[notification.notification_type]

				return {
					...notification,
					category: notificationCategories.value[notificationTypeObj?.category || 'other'],
					notification_type: notificationTypeObj,
					created: formatToFriendlyDateTime(notification.created_at),
				}
			})
		}



		const loadData = async () => {
			dataLoaded.value = false

			await loadNotifications()

			dataLoaded.value = true
		}

		loadData()

		const onReadAll = async () => {
			await updateNotificationsMarkAllRead()
			companyNotificationStore.reset()
			readStatus.value = true
		}
		const togglePreviewState = (state) => {
			isActivePreview.value = state
		}
		const onOpenPreview = async (id) => {
			previewId.value = id
			if (isActivePreview.value) {
				nextTick(async () => {
					await NotificationPreviewRef.value?.updateNotification()
				})
			}
			togglePreviewState(true)
		}

		const { start, stop } = useTimeoutFn(async () => {
			await loadNotifications()

			readStatus.value = false

			updateNotification()
		}, 120000)

		const updateNotification = () => {
			start()
			if (!readStatus.value) {
				setTimeout(async () => {
					await updateNotificationsMarkReadStatus({ ids: currentIds.value })
					readStatus.value = true
				}, 5000)
			}
		}

		onMounted(() => {
			updateNotification()
		})

		onUnmounted(() => {
			stop()
		})

		return {
			modalState,
			dataLoaded,
			readStatus,
			lastNotifications,
			selectedCompanyId,
			previewId,
			isActivePreview,
			NotificationPreviewRef,

			onReadAll,
			onOpenPreview,
			togglePreviewState,
		}
	},
}
</script>

<style lang="scss">
.app-sidebar-company-notifications {

	.ao-title-notification {
		font-weight: 700;
		font-size: 12px;
		line-height: 150%;
	}
	.ao-btn-notification {
		position: fixed;
		bottom: 0;
	}
	.ao-notification {
		border-radius: 4px;
		cursor: pointer;
		&:hover {
			background-color: #ecf1f7;
		}
	}
	.ao-info-notification {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: initial;
		display: -webkit-box;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
	}

}
</style>
